import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Check,
  FileText,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Send,
  Trash2,
  Users,
} from 'lucide-react'
import AdminPolicyFormModal from './AdminPolicyFormModal'
import AdminPolicySendInviteModal from './AdminPolicySendInviteModal'
import {
  deleteAdminPolicy,
  fetchAdminPolicies,
  regenerateAdminPolicyLink,
  setAdminPolicyActive,
} from '../../../services/adminPolicyService'

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return ''
  const mb = bytes / (1024 * 1024)
  if (mb >= 1) return `${mb.toFixed(1)} MB`
  return `${Math.round(bytes / 1024)} KB`
}

export default function AdminPoliciesPage() {
  const [policies, setPolicies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [copiedId, setCopiedId] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [sendTarget, setSendTarget] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const result = await fetchAdminPolicies({ search: search || undefined, per_page: 100 })
      setPolicies(result.policies)
    } catch (err) {
      setPolicies([])
      setError(err.response?.data?.message || 'Unable to load policy documents.')
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0)
    return () => clearTimeout(timer)
  }, [load])

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (policy) => {
    setEditing(policy)
    setFormOpen(true)
  }

  const copyLink = async (policy) => {
    try {
      await navigator.clipboard.writeText(policy.link)
      setCopiedId(policy.id)
      setTimeout(() => setCopiedId((c) => (c === policy.id ? null : c)), 2000)
    } catch {
      setError('Unable to copy the link automatically.')
    }
  }

  const toggleActive = async (policy) => {
    setBusyId(policy.id)
    try {
      const updated = await setAdminPolicyActive(policy.id, !policy.is_active)
      setPolicies((list) => list.map((p) => (p.id === updated.id ? updated : p)))
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update status.')
    } finally {
      setBusyId(null)
    }
  }

  const regenerate = async (policy) => {
    if (!window.confirm('Generate a new signing link? The previous link will stop working.')) return
    setBusyId(policy.id)
    try {
      const updated = await regenerateAdminPolicyLink(policy.id)
      setPolicies((list) => list.map((p) => (p.id === updated.id ? updated : p)))
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to regenerate the link.')
    } finally {
      setBusyId(null)
    }
  }

  const remove = async (policy) => {
    setBusyId(policy.id)
    try {
      await deleteAdminPolicy(policy.id)
      setPolicies((list) => list.filter((p) => p.id !== policy.id))
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to delete the document.')
    } finally {
      setBusyId(null)
      setConfirmDelete(null)
    }
  }

  const totalSignatures = useMemo(
    () => policies.reduce((sum, p) => sum + (p.signatures_count ?? 0), 0),
    [policies],
  )

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col p-6">
      <header className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Policy Documents</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Create policies, share a secure signing link, and track who has signed.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-lg bg-rapid-orange px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#E84A15]"
        >
          <Plus size={16} strokeWidth={2} aria-hidden />
          New document
        </button>
      </header>

      <div className="mt-4 flex items-center gap-3">
        <div className="relative w-full max-w-md">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name…"
            className="w-full rounded-lg bg-gray-100 py-2 pl-9 pr-3 text-sm outline-none placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-rapid-orange/30"
          />
        </div>
        {policies.length > 0 ? (
          <span className="text-xs text-gray-500">
            {policies.length} document{policies.length === 1 ? '' : 's'} · {totalSignatures} signature{totalSignatures === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>

      {error ? (
        <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="mt-4 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] border-collapse text-left">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">Document</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">Version</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">Signatures</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-6 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                    <Loader2 size={18} className="mr-2 inline animate-spin align-middle" aria-hidden />
                    Loading documents…
                  </td>
                </tr>
              ) : policies.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                    No policy documents yet. Create one to get started.
                  </td>
                </tr>
              ) : (
                policies.map((policy) => {
                  const busy = busyId === policy.id
                  return (
                    <tr key={policy.id} className="border-b border-gray-50 transition-colors last:border-0 hover:bg-gray-50/40">
                      <td className="px-6 py-4">
                        <Link to={`/admin/policies/${policy.id}`} className="group">
                          <p className="text-sm font-bold text-gray-900 transition-colors group-hover:text-rapid-orange">
                            {policy.name}
                          </p>
                        </Link>
                        <p className="mt-0.5 line-clamp-1 max-w-md text-xs text-gray-500">{policy.description}</p>
                        {policy.file?.file_name ? (
                          <p className="mt-1 flex items-center gap-1 text-[11px] text-gray-400">
                            <FileText size={12} aria-hidden />
                            {policy.file.file_name}
                            {formatBytes(policy.file.file_size) ? ` · ${formatBytes(policy.file.file_size)}` : ''}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">v{policy.version}</td>
                      <td className="px-6 py-4">
                        <Link
                          to={`/admin/policies/${policy.id}`}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-rapid-orange"
                        >
                          <Users size={14} aria-hidden />
                          {policy.signatures_count ?? 0}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => toggleActive(policy)}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold transition-colors ${
                            policy.is_active ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${policy.is_active ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                          {policy.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            title="Send signing invitation to staff"
                            aria-label="Send signing invitation to staff"
                            disabled={busy}
                            onClick={() => setSendTarget(policy)}
                            className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-rapid-orange"
                          >
                            <Send size={15} aria-hidden />
                          </button>
                          <button
                            type="button"
                            title="Copy signing link"
                            aria-label="Copy signing link"
                            disabled={busy}
                            onClick={() => copyLink(policy)}
                            className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                          >
                            {copiedId === policy.id ? <Check size={15} className="text-emerald-600" aria-hidden /> : <Link2 size={15} aria-hidden />}
                          </button>
                          <button
                            type="button"
                            title="Regenerate link"
                            aria-label="Regenerate link"
                            disabled={busy}
                            onClick={() => regenerate(policy)}
                            className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                          >
                            {busy ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <RefreshCw size={15} aria-hidden />}
                          </button>
                          <button
                            type="button"
                            title="Edit"
                            aria-label="Edit"
                            disabled={busy}
                            onClick={() => openEdit(policy)}
                            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-bold text-gray-700 transition-colors hover:bg-gray-100"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            aria-label="Delete"
                            disabled={busy}
                            onClick={() => setConfirmDelete(policy)}
                            className="rounded-md p-2 text-red-500 transition-colors hover:bg-red-50"
                          >
                            <Trash2 size={15} aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AdminPolicyFormModal
        isOpen={formOpen}
        policy={editing}
        onClose={() => setFormOpen(false)}
        onSaved={(saved) => {
          if (!saved) return
          setPolicies((list) => {
            const exists = list.some((p) => p.id === saved.id)
            if (exists) return list.map((p) => (p.id === saved.id ? saved : p))
            return [saved, ...list]
          })
        }}
      />

      <AdminPolicySendInviteModal
        isOpen={Boolean(sendTarget)}
        policy={sendTarget}
        onClose={() => setSendTarget(null)}
      />

      {confirmDelete ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button type="button" aria-label="Cancel" onClick={() => setConfirmDelete(null)} className="absolute inset-0 bg-gray-900/50" />
          <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-base font-bold text-gray-900">Delete this document?</h3>
            <p className="mt-1 text-sm text-gray-500">
              “{confirmDelete.name}” and its signing link will no longer be available. Recorded signatures are retained.
            </p>
            <div className="mt-4 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busyId === confirmDelete.id}
                onClick={() => remove(confirmDelete)}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-60"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
